import { useEffect, useState } from "react";
import { Button, Form, Row, Col, DatePicker, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";

import { eAMCAPPROVALSTATUS, eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TDealer, TTicket } from "../../../types";

import {
  AddEditTicketAMCApproval,
  GetSpecificDealerData,
  GetSpecificTicketAMCApproval,
} from "../../../utils/api.constant";
import { TResponseModel } from "../../../types/config";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../utils/input.validator";
import RowDisplay from "../../../components/data-display";
import DateUtils from "../../../utils/date";
import { CloseOutlined, CheckOutlined } from "@ant-design/icons";

type DrawerProps = {
  ticket?: TTicket;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * This component represents a form for creating or editing a service partner.
 * It handles the loading of data, including fetching specific service partner details,
 * countries, states, regions, and location types. It also handles form submission,
 * displaying toast messages, and resetting form fields.
 */
export default function AMCApproverForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonType, setButtonType] = useState<string>("");
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [dealerDetails, setDealerDetails] = useState<TDealer>();

  useEffect(() => {
    fetchSpecificDealer();
  }, [props?.ticket?.dealerId]);

  useEffect(() => {
    fetchSpecificTicket();
  }, []);

  const fetchSpecificTicket = async () => {
    if (props?.ticket?.id) {
      try {
        const payload = {
          data: {
            ticketId: props?.ticket?.id,
          },
        };

        const response: TResponseModel = await post(
          GetSpecificTicketAMCApproval,
          payload
        );
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode, description } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            const { amcFromDate, amcToDate, ...restData } = data;
            form.setFieldsValue({
              ...restData,

              amcFromDate: dayjs(amcFromDate),
              amcToDate: dayjs(amcToDate),
            });
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: description,
            });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
      }
    }
  };

  // Handle on submit event
  const onFinish = async (values: TDealer, buttonType: string) => {
    try {
      setIsLoading(true);
      const { amcFromDate, amcToDate, ...restValues } = values;
      const payload = {
        data: {
          ...props?.ticket,
          ...restValues,
          amcToDate: amcToDate
            ? DateUtils.getDateStringWithOutTimezone(
                new Date(amcToDate).toDateString()
              )
            : null,
          amcFromDate: amcFromDate
            ? DateUtils.getDateStringWithOutTimezone(
                new Date(amcFromDate).toDateString()
              )
            : null,
          ticketId: props?.ticket?.id,
          amcApprovalStatus:
            buttonType === "approve"
              ? eAMCAPPROVALSTATUS.APPROVED
              : eAMCAPPROVALSTATUS.REJECTED,
        },
      };

      const response = await post(AddEditTicketAMCApproval, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecificDealer = async () => {
    if (props?.ticket?.dealerId) {
      try {
        const payload = {
          data: {
            id: props?.ticket?.dealerId,
          },
        };

        const response: TResponseModel = await post(
          GetSpecificDealerData,
          payload
        );
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode, description } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            const {
              installationDate,
              amcFromDate,
              amcToDate,
              lastPM,
              customerName,
              warrantyDate,
              isAMCActive,
              ...restData
            } = data;
            form.setFieldsValue({
              ...restData,
              installationDate: installationDate
                ? dayjs(installationDate)
                : null,
              amcFromDate: amcFromDate ? dayjs(amcFromDate) : null,
              amcToDate: amcToDate ? dayjs(amcToDate) : null,
              lastPM: lastPM ? dayjs(lastPM) : null,
              warrantyDate: warrantyDate ? dayjs(warrantyDate) : null,
              customerName: customerName,
              isAMCActive,
            });
            setDealerDetails(data);
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: description,
            });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
      }
    }
  };

  /**
   * Resets the district field and fetches the list of cities based on the selected state
   */

  return (
    <FormLayout>
      <Form
        autoComplete="off"
        form={form}
        name="register"
        onFinish={(values) => onFinish(values, buttonType)} // Pass buttonType to onFinish
        layout="vertical"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>AMC Review</h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <RowDisplay
                heading="Dealer Code"
                content={dealerDetails?.dealerCode || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Depot Code"
                content={dealerDetails?.depotCode || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Dealer Name"
                content={dealerDetails?.dealerName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Dealer Address"
                content={
                  `${dealerDetails?.address}, ${dealerDetails?.districtName} - ${dealerDetails?.zipcode}, ${dealerDetails?.stateName}` ||
                  ""
                }
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Machine Type"
                content={dealerDetails?.machineTypeName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Model"
                content={dealerDetails?.productName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Machine Serial #"
                content={dealerDetails?.machineSerialNo || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <Form.Item
                name="amcFromDate"
                label="AMC From"
                dependencies={["amcToDate"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const amcToDate = getFieldValue("amcToDate");
                      if (
                        buttonType !== "reject" &&
                        value &&
                        amcToDate &&
                        value.isAfter(amcToDate)
                      ) {
                        return Promise.reject(
                          new Error(
                            "AMC From Date cannot be greater than AMC To Date"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                  {
                    required: buttonType !== "reject",
                    message: "Please select AMC starting date",
                  },
                ]}
              >
                <DatePicker
                  format="DD-MMM-YYYY"
                  placeholder="Select AMC from date"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="amcToDate"
                label="AMC To"
                dependencies={["amcFromDate"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const amcFromDate = getFieldValue("amcFromDate");
                      if (
                        buttonType !== "reject" &&
                        value &&
                        amcFromDate &&
                        value.isBefore(amcFromDate)
                      ) {
                        return Promise.reject(
                          new Error(
                            "AMC To Date cannot be less than AMC From Date"
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                  {
                    required: buttonType !== "reject",
                    message: "Please select AMC ending date",
                  },
                ]}
              >
                <DatePicker
                  format="DD-MMM-YYYY"
                  placeholder="Select AMC end date"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="remark"
                label="Remark"
                rules={[
                  {
                    required: buttonType === "reject",
                    validator: requiredFieldValidator("enter a remark"),
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter the remark..."
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              className="btnDimensions"
              type="primary"
              onClick={() => {
                setButtonType("approve"); // Set button type to "approve"
                form.submit();
              }}
              disabled={isLoading}
            >
              Approve
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              onClick={() => {
                setButtonType("reject"); // Set button type to "reject"
                form.submit();
              }}
              disabled={isLoading}
            >
              Reject
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
